/**
 * Product-Group Availability Bell — HIG-152 (Tobias 2026-06-09)
 * ------------------------------------------------------------------
 * Mirrors variant-availability.js's contextual sold-out bell, but ACROSS products.
 * A product-group member swatch is a SEPARATE product rendered as an <a>; it gets a
 * sold-out bell when the option value currently selected on THIS product (e.g.
 * Grösse = S) exists for that member but is sold out there — exactly like picking a
 * size that's OOS in one colour of a single multi-variant product.
 *
 * Conservative by design (Tobias's explicit preference: rather under-show OOS than
 * risk a false "sold out"):
 *   - member doesn't carry that option/value   -> NO bell (stays clickable)
 *   - member has the value available somewhere  -> NO bell
 *   - member has the value but every match OOS  -> bell
 * It never marks a member sold-out that a customer could still buy in that size.
 *
 * Why a separate `hp-pg-oos` class (not `is-unavailable`): product-group swatches are
 * ADJACENT .opt-label siblings, so the variant rules `.is-unavailable + .opt-label`
 * would bleed the badge onto the NEXT swatch. A distinct class stays fully isolated.
 *
 * Data: each <fieldset.option-selector--product-group> carries a
 *   <script type="application/json" data-pg-availability>
 *     { "<memberId>": { "names":[optName...], "variants":[{"o":[v1,v2..],"a":0|1}...] } }
 * and each member <a.opt-label> has data-pg-member-id="<memberId>".
 *
 * Selection is read LIVE from the real variant-picker(s)
 * (.option-selector:not(.option-selector--product-group)) by data-option (name) +
 * checked value, matched to member option names case-insensitively. Same-locale on
 * both sides, so the strings line up; a mismatch just yields no bell (safe).
 */
(function () {
  if (window.__hpPgAvailabilityInit) return;
  window.__hpPgAvailabilityInit = true;

  // {optionNameLower: selectedValue} from the real variant picker(s) on the page.
  // Excludes single-value options (data-single-option="true", e.g. a degenerate
  // per-colour "Farbe"): they aren't a real cross-product axis, and matching on them
  // would wrongly require a member's colour to equal the current product's colour,
  // hiding genuine size sold-outs. Only the shared axis (e.g. Grösse) is matched.
  // Bug 2026-09-20: Die Auswahl wird pro Gruppe NUR im eigenen Produkt-Kontext gelesen
  // (Sektion bzw. Auswahlfenster). Vorher zählte jeder Picker der Seite: Ein im
  // Auswahlfenster (quick-add-drawer) gewählter Wert eines FREMDEN Produkts überschrieb
  // die Auswahl des Hauptprodukts (gleicher Optionsname, z.B. «Grösse»).
  function scopeOf(el) {
    return el.closest('quick-add-drawer, .js-quickbuy, .shopify-section') || document;
  }

  function currentSelection(scope) {
    var sel = {};
    (scope || document).querySelectorAll('.option-selector:not(.option-selector--product-group):not([data-single-option="true"])').forEach(function (s) {
      var name = (s.getAttribute('data-option') || '').trim().toLowerCase();
      if (!name) return;
      var val = null;
      var radio = s.querySelector('.js-option:checked');
      if (radio) {
        val = radio.value;
      } else {
        var dd = s.querySelector('.custom-select__option[aria-selected="true"]');
        if (dd && dd.dataset.value !== '') val = dd.dataset.value;
      }
      if (val != null) sel[name] = val;
    });
    return sel;
  }

  // Varianten des Mitglieds, die auf der GETEILTEN Achse (z.B. Grösse) zur aktuellen
  // Auswahl passen. Leer = keine gemeinsame Achse oder Kombination gibt es dort nicht.
  function matchingVariants(data, sel) {
    var names = data.names || [];
    var shared = [];
    for (var i = 0; i < names.length; i++) {
      var key = (names[i] || '').trim().toLowerCase();
      if (Object.prototype.hasOwnProperty.call(sel, key)) shared.push({ i: i, key: key });
    }
    if (!shared.length) return [];
    return (data.variants || []).filter(function (v) {
      return shared.every(function (s) { return String(v.o[s.i]) === String(sel[s.key]); });
    });
  }

  function memberSoldOut(data, sel) {
    // Komplett ausverkauftes Mitglied: immer Glocke, egal ob es die gewählte Option gibt
    // (2026-10-02). Sonst zeigte z.B. Barebells Spread (nur 45 g) oder NOCCO Electrolyte
    // (nur 355 ml) nie eine Glocke, weil die geteilte Achse «55 g»/«330 ml» dort fehlt.
    // Bleibt konservativ: ein Mitglied ohne einzige kaufbare Variante ist nirgends kaufbar.
    var all = data.variants || [];
    if (all.length && all.every(function (v) { return !v.a; })) return true;
    var matching = matchingVariants(data, sel);
    if (!matching.length) return false; // keine geteilte Achse / Kombi gibt es dort nicht
    return matching.every(function (v) { return !v.a; }); // alle Treffer aus -> Glocke
  }

  // Varianten-ID, die beim Klick auf das Mitglied vorgewählt werden soll — oder null.
  // Bewusst KONSERVATIV (Grundhaltung dieser Datei): nur wenn der Treffer auch KAUFBAR
  // ist. Sonst bleibt die nackte Produkt-URL stehen, also exakt das alte Verhalten;
  // wir können den Kunden damit nie auf einer toten Grösse landen lassen.
  function vorwahlVariantId(data, sel) {
    var treffer = matchingVariants(data, sel);
    for (var i = 0; i < treffer.length; i++) {
      if (treffer[i].a && treffer[i].i) return treffer[i].i;
    }
    return null;
  }

  function refresh() {
    var groups = document.querySelectorAll('.option-selector--product-group');
    if (!groups.length) return;
    groups.forEach(function (fs) {
      var sel = currentSelection(scopeOf(fs));
      var dataEl = fs.querySelector('script[type="application/json"][data-pg-availability]');
      if (!dataEl) return;
      var map;
      try { map = JSON.parse(dataEl.textContent); } catch (e) { return; }
      fs.querySelectorAll('.opt-label[data-pg-member-id]').forEach(function (el) {
        var data = map[el.getAttribute('data-pg-member-id')];
        el.classList.toggle('hp-pg-oos', data ? memberSoldOut(data, sel) : false);

        // Gewählte Grösse mitnehmen (2026-08-02). Nur <a> (das aktuelle Produkt ist ein
        // <span>). Die unveränderte Original-URL wird einmalig gemerkt und ist immer die
        // Basis, damit sich bei mehrfachem Wechseln nie Parameter aufaddieren.
        if (el.tagName !== 'A') return;
        if (el.dataset.pgBase == null) el.dataset.pgBase = el.getAttribute('href') || '';
        var base = el.dataset.pgBase;
        if (!base) return;
        var vid = data ? vorwahlVariantId(data, sel) : null;
        el.setAttribute('href', vid ? base + (base.indexOf('?') > -1 ? '&' : '?') + 'variant=' + vid : base);
      });
    });
  }

  // Document-level delegation survives variant-picker.js replacing the picker element
  // on every variant change (the group fieldsets live OUTSIDE the picker and persist).
  function onChange(e) {
    var t = e.target;
    if (t && t.closest && t.closest('.option-selector:not(.option-selector--product-group)')) {
      setTimeout(refresh, 0);
    }
  }

  function start() {
    document.addEventListener('change', onChange);
    document.addEventListener('on:variant:change', function () { setTimeout(refresh, 0); });
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
